import React from "react";

import { Box, Typography } from "@mui/material";
import { Navigate, RouteObject, useParams } from "react-router";

// Application / System pages
import AdoptStackPage from "src/domains/application/pages/AdoptStackPage";
import ConnectionsPage from "src/domains/application/pages/ConnectionsPage";
import GitHubCallbackPage from "src/domains/application/pages/GitHubCallbackPage";
import ReviewServicesPage from "src/domains/application/pages/ReviewServicesPage";
import StackDetailsPage from "src/domains/application/pages/StackDetailsPage";
import StackEditPage from "src/domains/application/pages/StackEditPage";
import { LoginPage, RegisterPage } from "src/domains/auth/pages";
import { ProtectedRoute, PublicRoute } from "src/domains/auth/utils/routeUtils";
import Dashboard from "src/domains/dashboard/pages";
// Organization pages
import ErrorPage from "src/domains/error/pages/ErrorPage";
import InitializationLoading from "src/domains/loading/pages/InitializationLoading";
import OnboardingPage from "src/domains/onboarding/pages/OnboardingPage";
import {
  OrganizationList as OrganizationsPage,
  OrganizationDetails as OrganizationDetailsPage,
} from "src/domains/organization/pages";
import CreateOrganization from "src/domains/organization/pages/create";
// Project pages
import { ProjectsList as ProjectsPage } from "src/domains/project/pages";
import ProjectDetails from "src/domains/project/pages";
import CreateProjectPage from "src/domains/project/pages/create";
import RecoveryPage from "src/domains/recovery/pages/RecoveryPage";
import { ProvisionResourcePage } from "src/domains/resource/pages/ProvisionResourcePage";
import { ResourceDetailPage } from "src/domains/resource/pages/ResourceDetailPage";
// User pages
import UserProfile from "src/domains/user/pages";
import UserSettings from "src/domains/user/pages/settings";
import {
  LOADING,
  RECOVERY,
  ERROR,
  ONBOARDING,
  AUTH,
  DASHBOARD,
  ORGANIZATIONS,
  PROJECTS,
  USER,
  ACCOUNT,
  SETTINGS,
  STACKS,
  GITHUB,
} from "src/platform/constants/routes";
import DashboardLayout from "src/shell/layout/DashboardLayout";

const OrgMembersPlaceholder: React.FC = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h4">Organization Members</Typography>
  </Box>
);

const OrgSettingsPlaceholder: React.FC = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h4">Organization Settings</Typography>
  </Box>
);

/**
 * Redirect for the legacy Create Stack wizard route.
 * The 7-step wizard was replaced by the CreateApplicationDrawer on the
 * project page. Redirect to the project page with `?drawer=app` so the
 * drawer auto-opens.
 */
const CreateStackRedirect: React.FC = () => {
  const { orgAlias, projectAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
  }>();
  const target = PROJECTS.DETAILS.getPath(orgAlias ?? "", projectAlias ?? "");
  return <Navigate to={`${target}?drawer=app`} replace />;
};

/**
 * Redirect for the legacy Stacks list page.
 * The standalone list page was replaced by inline application display on
 * the project details page. Redirect to the project page.
 */
const StacksListRedirect: React.FC = () => {
  const { orgAlias, projectAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
  }>();
  const target = PROJECTS.DETAILS.getPath(orgAlias ?? "", projectAlias ?? "");
  return <Navigate to={target} replace />;
};

/**
 * State Machine Phase Routes - Public access during initialization/recovery
 */
const stateMachineRoutes: RouteObject[] = [
  {
    path: LOADING.path,
    element: <InitializationLoading />,
  },
  {
    path: RECOVERY.path,
    element: <RecoveryPage />,
  },
  {
    path: ERROR.path,
    element: <ErrorPage />,
  },
];

/**
 * Onboarding routes - Protected access during onboarding phase
 */
const onboardingRoutes: RouteObject[] = [
  {
    path: ONBOARDING.ROOT.path,
    element: (
      <ProtectedRoute>
        <OnboardingPage />
      </ProtectedRoute>
    ),
  },
  {
    path: ONBOARDING.CREATE_ORGANIZATION.path,
    element: (
      <ProtectedRoute>
        <CreateOrganization />
      </ProtectedRoute>
    ),
  },
  {
    path: ONBOARDING.SELECT_ORGANIZATION.path,
    element: (
      <ProtectedRoute>
        <OrganizationsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: ONBOARDING.COMPLETE.path,
    element: (
      <ProtectedRoute>
        <OnboardingPage />
      </ProtectedRoute>
    ),
  },
];

/**
 * Auth routes - public access
 */
const authRoutes: RouteObject[] = [
  {
    path: AUTH.ROOT.path,
    element: <PublicRoute restricted={true} />,
    children: [
      {
        path: AUTH.LOGIN.path,
        element: <LoginPage />,
      },
      {
        path: AUTH.REGISTER.path,
        element: <RegisterPage />,
      },
      // Root redirect to login
      {
        path: AUTH.ROOT.path,
        element: <Navigate to={AUTH.LOGIN.path} replace />,
      },
    ],
  },
];

/**
 * Main application routes (authenticated)
 */
const appRoutes: RouteObject[] = [
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      // Dashboard
      {
        path: DASHBOARD.path,
        element: <Dashboard />,
      },

      // Organizations — list + create
      {
        path: ORGANIZATIONS.ROOT.path,
        element: <OrganizationsPage />,
      },
      {
        path: ORGANIZATIONS.CREATE.path,
        element: <CreateOrganization />,
      },

      // Organization — detail + edit (same component, detects mode via URL)
      {
        path: ORGANIZATIONS.DETAILS.path,
        element: <OrganizationDetailsPage />,
      },
      {
        path: ORGANIZATIONS.EDIT.path,
        element: <OrganizationDetailsPage />,
      },

      // Organization sub-resources
      {
        path: ORGANIZATIONS.MEMBERS.path,
        element: <OrgMembersPlaceholder />,
      },
      {
        path: ORGANIZATIONS.ORG_SETTINGS.path,
        element: <OrgSettingsPlaceholder />,
      },
      {
        path: ORGANIZATIONS.VCS_CONNECTIONS.path,
        element: <ConnectionsPage />,
      },

      // Projects - Organization context
      {
        path: PROJECTS.LIST.path,
        element: <ProjectsPage />,
      },
      {
        path: PROJECTS.CREATE.path,
        element: <CreateProjectPage />,
      },
      {
        path: PROJECTS.DETAILS.path,
        element: <ProjectDetails />,
      },
      {
        path: PROJECTS.EDIT.path,
        element: <ProjectDetails />,
      },
      {
        path: PROJECTS.RESOURCES_NEW.path,
        element: <ProvisionResourcePage />,
      },
      {
        path: PROJECTS.RESOURCES_DETAIL.path,
        element: <ResourceDetailPage />,
      },

      // Account — canonical user profile, settings, and billing
      {
        path: ACCOUNT.PROFILE.path,
        element: <UserProfile />,
      },
      {
        path: ACCOUNT.SETTINGS.path,
        element: <UserSettings />,
      },
      // Legacy user paths — redirect to /account/*
      {
        path: USER.PROFILE.path,
        element: <Navigate to={ACCOUNT.PROFILE.path} replace />,
      },
      {
        path: USER.SETTINGS.path,
        element: <Navigate to={ACCOUNT.SETTINGS.path} replace />,
      },

      // Legacy /settings — redirect to /account/settings
      {
        path: SETTINGS.path,
        element: <Navigate to={ACCOUNT.SETTINGS.path} replace />,
      },

      // Stacks (infrastructure stacks, backed by /api/v1/applications)
      // Legacy stacks list page — replaced by inline display on project page
      {
        path: "/organizations/:orgAlias/projects/:projectAlias/stacks",
        element: <StacksListRedirect />,
      },
      // Legacy wizard route — replaced by CreateApplicationDrawer on project page
      {
        path: STACKS.CREATE.path,
        element: <CreateStackRedirect />,
      },
      // Adopt existing repo — AI inspection + rusha.yml generation
      {
        path: STACKS.ADOPT.path,
        element: <AdoptStackPage />,
      },
      {
        path: STACKS.DETAILS.path,
        element: <StackDetailsPage />,
      },
      {
        path: STACKS.EDIT.path,
        element: <StackEditPage />,
      },
      {
        path: STACKS.SERVICES.path,
        element: <ReviewServicesPage />,
      },

      // Backward-compatible redirect: /systems/* → /stacks/*
      {
        path: "/organizations/:orgAlias/projects/:projectAlias/systems",
        element: (
          <Navigate
            to="/organizations/:orgAlias/projects/:projectAlias/stacks"
            replace
          />
        ),
      },
      {
        path: "/organizations/:orgAlias/projects/:projectAlias/systems/:sysAlias",
        element: (
          <Navigate
            to="/organizations/:orgAlias/projects/:projectAlias/stacks/:sysAlias"
            replace
          />
        ),
      },
      {
        path: "/organizations/:orgAlias/projects/:projectAlias/systems/create",
        element: <CreateStackRedirect />,
      },
      {
        path: "/organizations/:orgAlias/projects/:projectAlias/systems/:sysAlias/edit",
        element: (
          <Navigate
            to="/organizations/:orgAlias/projects/:projectAlias/stacks/:sysAlias/edit"
            replace
          />
        ),
      },

      // Root redirect — send to org selection (or org workspace if session active).
      // Dashboard component handles the smart redirect based on session state.
      {
        path: "/",
        element: <Navigate to={DASHBOARD.path} replace />,
      },

      // Fallback for unmatched paths (404 handling)
      {
        path: "*",
        element: <Navigate to={DASHBOARD.path} replace />,
      },
    ],
  },
];

/**
 * Combined routes configuration for the application
 * Order matters: state machine routes first, then auth, then onboarding, then app routes
 */
export const routes: RouteObject[] = [
  // State machine phase routes (public access)
  ...stateMachineRoutes,

  // Auth routes (public access)
  ...authRoutes,

  // GitHub App callback route (public access)
  {
    path: GITHUB.CALLBACK.path,
    element: <GitHubCallbackPage />,
  },

  // Onboarding routes (protected access)
  ...onboardingRoutes,

  // Main application routes (protected access)
  ...appRoutes,

  // Global redirect to loading (start state machine)
  {
    path: "*",
    element: <Navigate to={LOADING.path} replace />,
  },
];

export default routes;
